import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, LockIcon } from 'lucide-react';
import { HealthConsentModal } from './HealthConsentModal';
import { SettingsGroup, SettingsRow, SettingsToggleRow } from './SettingsList';
import { SectionTitle } from './ui/Card';
import { Switch } from './ui/Switch';
import { biomarkerDefs } from '../data/biomarkers';
import { privacyPrefs, type PrivacyPref } from '../data/settings';
import { metricGroupsForSport, sports, standaloneMetrics } from '../data/sportMetrics';
import { profileFieldsForSport } from '../data/sportProfileFields';
import { displaySportField, formatMetricGroup } from '../utils/sportFields';
import { useBiomarkers } from '../contexts/BiomarkersContext';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import { useSettings } from '../contexts/SettingsContext';
import { formatMetric } from '../utils/units';
interface Item {
  id: string;
  label: string;
  detail?: string;
  checked: boolean;
  toggle: (next: boolean) => void;
}

/**
 * "O que aparece no meu perfil público" — one toggle per category, then
 * granular selection inside sport, body and biomarker categories.
 * Shared by Edit Profile and Privacy. Health categories need explicit consent.
 * Everything except achievements, badges and streak starts OFF.
 */
export function PublicVisibilityControls() {
  const {
    t,
    tl,
    locale
  } = useLocale();
  const navigate = useNavigate();
  const settings = useSettings();
  const metrics = useMetrics();
  const bio = useBiomarkers();
  const [consentFor, setConsentFor] = useState<string | null>(null);
  const categories = privacyPrefs.filter(pref => pref.scope === 'public' && (!pref.parent || settings.privacy[pref.parent]));
  const pending = privacyPrefs.find(pref => pref.id === consentFor);
  const itemsFor = (pref: PrivacyPref): Item[] => {
    if (pref.granular === 'sport') {
      // Numeric metrics and categorical profile fields of each sport — all OFF by default.
      return sports.flatMap(sport => [...standaloneMetrics(sport).filter(metric => metric.value > 0).map(metric => ({
        id: metric.id,
        label: `${t(`category.${sport}`)} · ${tl(metric.label)}`,
        detail: formatMetric(metric.kind, metric.value, metrics.unitSystem),
        checked: metrics.isPublic(metric.id),
        toggle: () => metrics.togglePublic(metric.id)
      })),
      // Grouped records (Boxing cartel) are one public choice, never split.
      ...metricGroupsForSport(sport).filter(group => group.members.some(member => member.value > 0)).map(group => ({
        id: group.id,
        label: `${t(`category.${sport}`)} · ${tl(group.label)}`,
        detail: formatMetricGroup(group.members.map(member => member.id), Object.fromEntries(group.members.map(member => [member.id, member.value])), locale),
        checked: metrics.isPublic(group.id),
        toggle: () => metrics.togglePublic(group.id)
      })), ...profileFieldsForSport(sport).map(field => ({
        field,
        text: displaySportField(field, metrics.sportFields, locale, tl)
      })).filter(({
        text
      }) => text !== '').map(({
        field,
        text
      }) => ({
        id: field.id,
        label: `${t(`category.${sport}`)} · ${tl(field.label)}`,
        detail: text,
        checked: metrics.isPublic(field.id),
        toggle: () => metrics.togglePublic(field.id)
      }))]);
    }
    if (pref.granular === 'body') {
      return metrics.bodyMetrics.filter(metric => !metrics.isHidden(metric.id)).map(metric => ({
        id: metric.id,
        label: t(`metrics.${metric.id}`),
        detail: formatMetric(metric.kind, metric.value, metrics.unitSystem),
        checked: metrics.isPublic(metric.id),
        toggle: () => metrics.togglePublic(metric.id)
      }));
    }
    if (pref.granular === 'biomarker') {
      return biomarkerDefs.map(def => ({
        id: def.id,
        label: tl(def.name),
        checked: bio.isPublic(def.id),
        toggle: (next: boolean) => bio.setPublic(def.id, next)
      }));
    }
    return [];
  };
  return <section data-id="element-1266" data-mp="64lgdpya61vm">
      <SectionTitle data-id="element-1267" data-mp="64bry6svw2s1">{t('visibility.title')}</SectionTitle>
      <p className="-mt-1 mb-2 text-[11px] leading-snug text-muted" data-id="element-1268" data-mp="3obhugwmc1a77">
        {t('visibility.subtitle')}
      </p>

      <SettingsGroup data-id="element-1269" data-mp="3sf7ldsqhc3ve">
        {categories.map(pref => {
        const on = settings.privacy[pref.id];
        const items = on ? itemsFor(pref) : [];
        return <React.Fragment key={pref.id} data-id="element-1270" data-mp="3jdzlj68beuz7">
              <SettingsToggleRow label={pref.parent ? `— ${tl(pref.label)}` : tl(pref.label)} hint={tl(pref.hint)} checked={on} emphasis={pref.health} onChange={next => {
            if (next && pref.health) setConsentFor(pref.id);else settings.togglePrivacy(pref.id);
          }} data-id="element-1271" data-mp="3jf1744myizv6" />
              {items.length > 0 && <li className="border-b border-sand-line pb-2 last:border-b-0" data-id="element-1272" data-mp="3buqlw4pm2anc">
                  {items.every(item => !item.checked) ? <div className="mb-1 ml-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-card border border-gold/35 bg-white/60 px-2 py-2" data-id="element-1273" data-mp="37148ubfjc4r0">
                      <p className="min-w-0 flex-1 basis-[160px] text-[11.5px] leading-snug text-deep" data-id="element-1274" data-mp="3fu8xg9vjl9di">
                        {t('visibility.noneSelected')}
                      </p>
                      <button type="button" onClick={() => {
                const first = document.querySelector<HTMLElement>(`#pick-${pref.id} button`);
                first?.focus();
                first?.scrollIntoView({
                  block: 'center',
                  behavior: 'smooth'
                });
              }} className="min-h-[36px] shrink-0 rounded-pill border border-deep/20 px-2 text-[11.5px] font-medium text-deep transition-colors duration-150 ease-brand hover:border-gold" data-id="element-1275" data-mp="3pswzhz74tdvk">
                        {t('visibility.chooseMetrics')}
                      </button>
                    </div> : <p className="mb-1 pl-3 text-[10.5px] font-medium text-muted" data-id="element-1276" data-mp="3840o6ifq02bj">
                      {t('visibility.pickEach')}
                    </p>}
                  <ul id={`pick-${pref.id}`} className="ml-3 rounded-card bg-sand-deep/50 px-2" data-id="element-1277" data-mp="3cwtuhaqu7ipq">
                    {items.map(item => <li key={item.id} className="flex min-h-[48px] items-center gap-2 border-b border-sand-line/70 py-1 last:border-b-0" data-id="element-1278" data-mp="3mtgclrrs3vo2">
                        <span className="min-w-0 flex-1" data-id="element-1279" data-mp="1g5pid55u6g0y">
                          <span className="block truncate text-[12.5px] font-medium text-deep" data-id="element-1280" data-mp="1b5l0pbt6zf95">
                            {item.label}
                          </span>
                          {item.detail && <span className="bee-num block text-[11px] text-muted" data-id="element-1281" data-mp="117ngczege2lt">
                              {item.detail}
                            </span>}
                        </span>
                        <span className={`flex shrink-0 items-center gap-1 text-[10.5px] font-medium ${item.checked ? 'text-gold-deep' : 'text-muted'}`} data-id="element-1282" data-mp="1tvt1hn9f5tm6">
                          {item.checked ? <EyeIcon size={12} aria-hidden="true" data-id="element-1283" data-mp="2hmovmr1cl0wl" /> : <LockIcon size={12} aria-hidden="true" data-id="element-1284" data-mp="2ctvra49sjul1" />}
                          {t(item.checked ? 'metrics.publicOn' : 'metrics.publicOff')}
                        </span>
                        <Switch checked={item.checked} onChange={item.toggle} label={item.label} data-id="element-1285" data-mp="1pzn9ialczq8y" />
                      </li>)}
                  </ul>
                </li>}
            </React.Fragment>;
      })}
      </SettingsGroup>

      <p className="mt-2 flex items-start gap-1 px-1 text-[11px] leading-snug text-muted" data-id="element-1286" data-mp="28vwvrl6ezn41">
        <LockIcon size={12} className="mt-[1px] shrink-0" aria-hidden="true" data-id="element-1287" data-mp="1uxyjclbnm0r6" />
        {t('visibility.healthNote')}
      </p>

      <div className="mt-3" data-id="element-1288" data-mp="1zuoxv47t8oah">
        <SettingsGroup data-id="element-1289" data-mp="1zy31sgw4h5k1">
          <SettingsRow label={t('visibility.preview')} icon={<EyeIcon size={17} data-id="element-1291" data-mp="jkcwj6t9ob8t" />} onClick={() => navigate('/perfil/publico')} data-id="element-1290" data-mp="ji9ufoeuarnq" />
        </SettingsGroup>
      </div>

      <HealthConsentModal subject={pending ? tl(pending.label) : null} onClose={() => setConsentFor(null)} onAccept={() => {
      if (consentFor) settings.togglePrivacy(consentFor);
      setConsentFor(null);
    }} data-id="element-1292" data-mp="gx2d35lqm2ma" />
    </section>;
}